<template>
  <div class="animated fadeIn random-sets-view">
    <div class="main-container">
      <b-card no-body class="border-0 shadow-sm mb-3 bg-dark text-white overflow-hidden sets-card">
        <b-card-body class="p-4">
          <div class="sets-header">
            <div class="sets-toolbar">
              <div class="toolbar-left">
                <div class="controls-panel controls-panel-grow">
                  <div class="controls-label">Filters</div>
                  <div class="filter-row">
                    <button
                      v-for="filter in statusFilters"
                      :key="filter.value"
                      type="button"
                      class="filter-pill"
                      :class="[{ active: activeFilter === filter.value }, `filter-${filter.value}`]"
                      @click="activeFilter = filter.value"
                    >
                      <i v-if="filter.icon" :class="filter.icon"></i>
                      <span>{{ filter.label }}</span>
                    </button>
                  </div>
                  <div class="tier-filter">
                    <small>Tier</small>
                    <b-form-select v-model="selectedTier" :options="tierOptions" size="sm"></b-form-select>
                  </div>
                  <div class="tier-filter">
                    <small>Per page</small>
                    <b-form-select v-model="perPage" :options="perPageOptions" size="sm"></b-form-select>
                  </div>
                </div>

              </div>
              <div class="toolbar-right">
                <div class="controls-panel controls-panel-actions">
                  <div class="controls-label">Actions</div>
                  <div class="action-row">
                    <b-button
                      v-if="hasPermission(PvpRandomSetsEdit)"
                      variant="info"
                      size="sm"
                      class="mr-2"
                      @click="openAddModal"
                    >
                      <i class="fa fa-plus mr-1"></i> Add Set
                    </b-button>
                    <b-button
                      v-if="hasPermission(PvpRandomSetsEdit)"
                      variant="outline-info"
                      size="sm"
                      class="mr-2"
                      @click="uploadRandomTeams"
                    >
                      <i class="fa fa-upload mr-1"></i> Upload Random Teams
                    </b-button>
                    <b-button
                      v-if="hasPermission(PvpRandomSetsEdit)"
                      variant="outline-secondary"
                      size="sm"
                      class="mr-2"
                      @click="openRulesModal"
                    >
                      <i class="fa fa-info-circle mr-1"></i> View Set Rules
                    </b-button>
                    <b-button
                      v-if="hasPermission(PvpRandomSetsEdit)"
                      variant="outline-warning"
                      size="sm"
                      class="mr-2"
                      @click="applyRules"
                    >
                      <i class="fa fa-magic mr-1"></i> Apply Set Rules
                    </b-button>

                    <b-input-group size="sm" class="search-box-group action-search">
                      <b-input-group-prepend class="search-box-prepend">
                        <i class="fa fa-search"></i>
                      </b-input-group-prepend>
                      <b-form-input
                        v-model="quickFilter"
                        placeholder="Pokemon, tier, ability, moves, items..."
                        class="search-box-input"
                      />
                      <b-input-group-append v-if="quickFilter">
                        <b-button variant="outline-secondary" class="search-clear" @click="quickFilter = ''">
                          <i class="fa fa-times small"></i>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <div class="sets-list-wrapper">
        <div v-if="loading" class="loading-overlay">
          <b-spinner label="Loading..." variant="info"></b-spinner>
          <div class="text-muted mt-2">Loading random sets...</div>
        </div>

        <div v-if="!filteredEntries.length && !loading" class="text-center py-5 text-muted">
          <i class="fa fa-random fa-3x mb-3 opacity-20"></i>
          <div class="h5">No random sets found.</div>
        </div>

        <div v-else class="sets-pagination">
          <div class="text-muted small">
            Showing {{ pagedEntries.length }} of {{ filteredEntries.length }} sets
          </div>
          <b-pagination
            v-model="currentPage"
            :per-page="perPage"
            :total-rows="filteredEntries.length"
            size="sm"
            class="mb-2"
          ></b-pagination>
        </div>

        <b-row v-if="pagedEntries.length" class="sets-grid">
          <b-col
            v-for="entry in pagedEntries"
            :key="entry.uid"
            cols="12"
            md="6"
            xl="4"
            class="mb-3"
          >
            <b-card no-body class="set-card border-0 shadow-sm bg-dark text-white h-100">
              <div class="set-card-header">
                <div class="set-header-main">
                  <span class="set-icon">
                    <img
                      :src="getPokemonSpriteUrl(entry.monster_id)"
                      :alt="entry.monster_name"
                      :data-fallback-id="getPokemonFallbackId(entry.monster_id)"
                      @error="handleIconError"
                    />
                    <i class="fa fa-question" aria-hidden="true"></i>
                  </span>
                  <div>
                    <div class="set-name">{{ entry.monster_name || `#${entry.monster_id}` }}</div>
                    <div class="set-meta">UID #{{ entry.uid }} - Tier {{ entry.tier_name }}</div>
                  </div>
                </div>
                <div class="set-actions">
                  <span class="set-flag-badge" :class="flagBadgeClass(entry)">{{ flagBadgeText(entry) }}</span>

                  <span class="set-badge" :class="entry.active ? 'badge-live' : 'badge-muted'">
                    {{ entry.active ? 'Active' : 'Inactive' }}
                  </span>
                  <b-button
                    v-if="hasPermission(PvpRandomSetsEdit)"
                    variant="link"
                    class="set-action-btn"
                    @click="openEditModal(entry)"
                    title="Edit Set"
                  >
                    <i class="fa fa-pencil"></i>
                  </b-button>
                  <b-button
                    v-if="hasPermission(PvpRandomSetsEdit)"
                    variant="link"
                    class="set-action-btn"
                    @click="openCloneModal(entry)"
                    title="Clone Set"
                  >
                    <i class="fa fa-clone"></i>
                  </b-button>
                  <b-button
                    v-if="hasPermission(PvpRandomSetsEdit)"
                    variant="link"
                    class="set-action-btn"
                    :disabled="deletingId === entry.uid"
                    @click="confirmDelete(entry)"
                    title="Delete Set"
                  >
                    <i class="fa" :class="deletingId === entry.uid ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
                  </b-button>
                </div>
              </div>

              <div class="set-details">
                <div class="set-row">
                  <span class="set-label">Level</span>
                  <span class="set-value">{{ getTierLevel(entry.tier_id) }}</span>
                </div>
                <div class="set-row">
                  <span class="set-label">Ability</span>
                  <span class="set-value">{{ entry.ability_name || 'Unknown' }}</span>
                </div>
                <div class="set-row">
                  <span class="set-label">Flags</span>
                  <span class="set-value">{{ formatFlags(entry) }}</span>
                </div>
                <div class="set-row">
                  <span class="set-label">Moves</span>
                  <span class="set-value">{{ formatMoves(entry) }}</span>
                </div>
                <div class="set-row">
                  <span class="set-label">Items</span>
                  <span class="set-value">{{ formatItems(entry) }}</span>
                </div>
                <div class="set-row">
                  <span class="set-label">IV</span>
                  <span class="set-value">{{ formatIvs(entry) }}</span>
                </div>
                <div class="set-row">
                  <span class="set-label">EV</span>
                  <span class="set-value">{{ formatEvs(entry) }}</span>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-modal
      v-model="showAddModal"
      size="lg"
      scrollable
      hide-footer
      header-bg-variant="dark"
      header-text-variant="white"
      body-bg-variant="darker"
      content-class="border-0 shadow-lg bg-dark rounded-lg"
    >
      <template #modal-title>
        <span class="font-weight-bold">{{ editingId ? 'Edit' : 'Add' }} Random Battle Set</span>
      </template>

      <div class="p-3 add-modal-body">
        <div class="preview-card mb-4">
          <div class="preview-title">Preview</div>
          <div class="preview-details">
            <div class="preview-row">
              <span class="preview-label">Pokemon</span>
              <span class="preview-value">{{ formPokemonLabel }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">Ability</span>
              <span class="preview-value">{{ formAbilityLabel }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">Moves</span>
              <span class="preview-value">{{ formMovesLabel }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">Items</span>
              <span class="preview-value">{{ formItemsLabel }}</span>
            </div>
          </div>
        </div>

        <div class="form-panel" :class="{ 'is-invalid': invalidShake }">
          <b-row class="form-row">
            <b-col cols="12" md="6">
              <b-form-group label="Pokemon" label-class="form-label">
                <search-pokemon
                  :values="monsters"
                  v-model="form.monsterId"
                  placeholder="Pokemon"
                  :class="{ 'is-invalid': monsterState === false }"
                  @change="onMonsterChange"
                  @selectedItem="onMonsterSelected"
                ></search-pokemon>
                <b-form-invalid-feedback v-if="monsterState === false" class="d-block">
                  Select a valid Pokemon from the list.
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Tier" label-class="form-label" :state="tierState">
                <b-form-select
                  v-model="form.tierId"
                  :options="tierOptions"
                  :state="tierState"
                ></b-form-select>
                <b-form-invalid-feedback v-if="tierState === false">
                  Select a tier.
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row">
            <b-col cols="12" md="6">
              <b-form-group label="Ability" label-class="form-label" :state="abilityState">
                <b-form-select
                  v-model="form.abilityName"
                  :options="abilitySelectOptions"
                  :state="abilityState"
                ></b-form-select>
                <b-form-invalid-feedback v-if="abilityState === false" class="d-block">
                  Ability must match the selected Pokemon.
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Status Flags" label-class="form-label">
                <div class="flag-tags">
                  <button
                    type="button"
                    class="flag-tag"
                    :class="[{ active: form.isMega }, 'flag-mega']"
                    @click="toggleFlag('isMega')"
                  >
                    <i class="fa fa-diamond"></i> Mega
                  </button>
                  <button
                    type="button"
                    class="flag-tag"
                    :class="[{ active: form.isZMove }, 'flag-zmove']"
                    @click="toggleFlag('isZMove')"
                  >
                    <i class="fa fa-star"></i> Z-Move
                  </button>
                  <button
                    type="button"
                    class="flag-tag"
                    :class="[{ active: form.isHazard }, 'flag-hazard']"
                    @click="toggleFlag('isHazard')"
                  >
                    <i class="fa fa-fire"></i> Hazard
                  </button>
                  <button
                    type="button"
                    class="flag-tag"
                    :class="[{ active: form.active }, 'flag-active']"
                    @click="toggleFlag('active')"
                  >
                    <i class="fa fa-check-circle"></i> Active
                  </button>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row">
            <b-col cols="12">
              <b-form-group label="IVs" label-class="form-label">
                <div class="stat-grid">
                  <div v-for="stat in ivFields" :key="`iv-${stat.key}`" class="stat-cell">
                    <label>{{ stat.label }}</label>
                    <b-form-input type="number" v-model.number="form.ivs[stat.key]" min="0" max="31"></b-form-input>
                  </div>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row">
            <b-col cols="12">
              <b-form-group label="EVs" label-class="form-label">
                <div class="stat-grid">
                  <div v-for="stat in evFields" :key="`ev-${stat.key}`" class="stat-cell">
                    <label>{{ stat.label }}</label>
                    <b-form-input type="number" v-model.number="form.evs[stat.key]" min="0" max="255"></b-form-input>
                  </div>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row">
            <b-col cols="12">
              <b-form-group label="Moves (at least one)" label-class="form-label">
                <div class="moves-list">
                  <div
                    v-for="(move, index) in form.moves"
                    :key="`move-${index}`"
                    class="move-row"
                  >
                    <div class="list-field">
                      <search-pokemon
                        :values="moves"
                        v-model="form.moves[index]"
                        :placeholder="`Move ${index + 1}`"
                        :class="{ 'is-invalid': moveStates[index] === false }"
                        @change="onMoveChange(index, $event)"
                        @selectedItem="onMoveSelected(index)"
                      ></search-pokemon>
                    </div>
                    <b-button
                      variant="link"
                      size="sm"
                      class="ml-2 mini-icon-btn"
                      :disabled="form.moves.length === 1"
                      @click="removeMove(index)"
                    >
                      <i class="fa fa-minus"></i>
                    </b-button>
                  </div>
                  <b-button
                    variant="link"
                    size="sm"
                    class="mt-2 action-link"
                    :disabled="form.moves.length >= 4"
                    @click="addMove"
                  >
                    <i class="fa fa-plus"></i>
                    <span>Add move</span>
                  </b-button>
                </div>
                <b-form-invalid-feedback v-if="movesHaveError" class="d-block">
                  Select valid moves from the list.
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row">
            <b-col cols="12">
              <b-form-group label="Held Items (optional)" label-class="form-label">
                <div class="moves-list">
                  <div
                    v-for="(item, index) in form.heldItems"
                    :key="`item-${index}`"
                    class="move-row"
                  >
                    <span class="item-icon">
                      <img
                        :src="getItemSpriteUrl(form.heldItems[index].name)"
                        :alt="getItemLabelFromName(form.heldItems[index].name)"
                        @error="handleIconError"
                      />
                      <i class="fa fa-question" aria-hidden="true"></i>
                    </span>
                    <div class="list-field">
                      <search-item
                        :values="items"
                        v-model="form.heldItems[index].name"
                        placeholder="Item"
                        :class="{ 'is-invalid': heldItemStates[index] === false }"
                      ></search-item>
                    </div>
                    <b-button
                      variant="link"
                      size="sm"
                      class="ml-2 mini-icon-btn"
                      :disabled="form.heldItems.length === 1"
                      @click="removeItem(index)"
                    >
                      <i class="fa fa-minus"></i>
                    </b-button>
                  </div>
                  <b-button
                    variant="link"
                    size="sm"
                    class="mt-2 action-link"
                    @click="addItem"
                  >
                    <i class="fa fa-plus"></i>
                    <span>Add item</span>
                  </b-button>
                </div>
                <b-form-invalid-feedback v-if="heldItemsHaveError" class="d-block">
                  Items must be selected from the list.
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </div>

      <div class="p-3 border-top border-dark-accent d-flex justify-content-end">
        <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showAddModal = false">Cancel</b-button>
        <b-button
          variant="info"
          size="sm"
          :disabled="saving"
          :class="{ 'btn-disabled-soft': !isFormValid && !saving }"
          @click="submitSet"
        >
          <i class="fa" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
          <span class="ml-1">{{ saving ? 'Saving...' : 'Save Set' }}</span>
        </b-button>
      </div>
    </b-modal>

    <b-modal
      v-model="showRulesModal"
      size="xl"
      modal-class="random-battle-rules-modal"
      scrollable
      hide-footer
      header-bg-variant="dark"
      header-text-variant="white"
      body-bg-variant="darker"
      content-class="border-0 shadow-lg bg-dark rounded-lg"
    >
      <template #modal-title>
        <span class="font-weight-bold">Random Battle Rules</span>
      </template>

      <div class="p-3 rules-modal-body">
        <div class="rules-panel mb-3">
          <div class="rules-title">Applied Rules</div>
          <ol class="rules-list">
            <li v-for="rule in randomBattleRuleDescriptions" :key="rule">{{ rule }}</li>
          </ol>
        </div>

        <b-row class="blacklist-row">
          <b-col cols="12" xl="4" class="mb-3">
            <b-form-group label="Move Blacklist" label-class="form-label">
              <b-form-textarea
                v-model="rulesForm.moves"
                rows="18"
                max-rows="24"
                class="rules-textarea"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
          <b-col cols="12" xl="4" class="mb-3">
            <b-form-group label="Ability Blacklist" label-class="form-label">
              <b-form-textarea
                v-model="rulesForm.abilities"
                rows="18"
                max-rows="24"
                class="rules-textarea"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
          <b-col cols="12" xl="4" class="mb-3">
            <b-form-group label="Item Blacklist" label-class="form-label">
              <b-form-textarea
                v-model="rulesForm.items"
                rows="18"
                max-rows="24"
                class="rules-textarea"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div class="p-3 border-top border-dark-accent d-flex justify-content-end">
        <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showRulesModal = false">Close</b-button>
        <b-button
          variant="info"
          size="sm"
          :disabled="rulesSaving"
          @click="saveRulesBlacklists"
        >
          <i class="fa" :class="rulesSaving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
          <span class="ml-1">{{ rulesSaving ? 'Saving...' : 'Save Blacklists' }}</span>
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Monsters from '@/views/pokemon/_monsters'
import Moves from '@/views/pokemon/_moves'
import Items from '@/views/user/inventory/_items'
import SearchPokemon from '@/views/pokemon/Search'
import SearchItem from '@/views/user/inventory/Search'
import { mapGetters } from 'vuex'

const HAZARD_MOVES = new Set([
  'stealth rock',
  'stone axe',
  'spikes',
  'ceaseless edge',
  'sticky web',
  'toxic spikes'
])

const RANDOM_BATTLE_RULE_DESCRIPTIONS = [
  'Sets that take 25% or 50% Stealth Rock damage are adjusted to odd HP.',
  'Substitute-only sets without Sitrus Berry, Belly Drum, or berry-related abilities are adjusted to odd HP.',
  'Unburden + Sitrus Berry sets are adjusted to even HP.',
  'Harvest + Sitrus Berry sets are adjusted to even HP.',
  'Cud Chew sets are adjusted to even HP.',
  'Cheek Pouch sets are adjusted to even HP.',
  'Belly Drum-only sets without Sitrus Berry, Substitute, or Gluttony are adjusted to odd HP.',
  'Belly Drum + Substitute sets are adjusted to even HP.',
  'Belly Drum + Gluttony sets are adjusted to even HP.',
  'Belly Drum + Sitrus Berry sets are adjusted to even HP.',
  'Trick Room or Gyro Ball sets get 0 Speed IV and 0 Speed EV.',
  'Nihilego always gets 52 Special Defense EVs.',
  'Sets flagged as both Mega and Z-Move are deactivated.',
  'Hazard sets are marked when they include a hazard move or the selected Toxic Debris ability.',
  'Sets with blacklisted moves are deactivated.',
  'Sets with blacklisted items are deactivated.',
  'Sets with a blacklisted selected ability are deactivated; Ability1, Ability2, and Ability3 use the same blacklist.'
]

export default {
  name: 'RandomSets',
  components: {
    'search-pokemon': SearchPokemon,
    'search-item': SearchItem
  },
  data() {
    return {
      quickFilter: '',
      activeServer: 'gold',
      loading: false,
      saving: false,
      rulesSaving: false,
      deletingId: null,
      editingId: null,
      suspendHazardAutoDetect: false,
      invalidShake: false,
      validationActive: false,
      activeFilter: 'all',
      selectedTier: '',
      currentPage: 1,
      perPage: 60,
      perPageOptions: [
        { value: 30, text: '30' },
        { value: 60, text: '60' },
        { value: 120, text: '120' }
      ],
      showAddModal: false,
      showRulesModal: false,
      monsters: Object.freeze(Monsters),
      moves: Object.freeze(Moves),
      items: Object.freeze(Items),
      randomBattleRuleDescriptions: Object.freeze(RANDOM_BATTLE_RULE_DESCRIPTIONS),
      monsterInput: '',
      moveInputs: [''],
      rulesForm: {
        moves: '',
        abilities: '',
        items: ''
      },
      statusFilters: [
        { label: 'All', value: 'all', icon: 'fa fa-th-large' },
        { label: 'Active', value: 'active', icon: 'fa fa-bolt' },
        { label: 'Mega', value: 'mega', icon: 'fa fa-diamond' },
        { label: 'Z-Move', value: 'zmove', icon: 'fa fa-star' },
        { label: 'Hazard', value: 'hazard', icon: 'fa fa-fire' }
      ],
      ivFields: [
        { key: 'atk', label: 'ATK' },
        { key: 'def', label: 'DEF' },
        { key: 'spd', label: 'SPD' },
        { key: 'spatk', label: 'SPATK' },
        { key: 'spdef', label: 'SPDEF' },
        { key: 'hp', label: 'HP' }
      ],
      evFields: [
        { key: 'atk', label: 'ATK' },
        { key: 'def', label: 'DEF' },
        { key: 'spd', label: 'SPD' },
        { key: 'spatk', label: 'SPATK' },
        { key: 'spdef', label: 'SPDEF' },
        { key: 'hp', label: 'HP' }
      ],
      form: {
        monsterId: '',
        abilityName: '',
        tierId: '',
        isMega: false,
        isZMove: false,
        isHazard: false,
        active: true,
        ivs: { atk: 31, def: 31, spd: 31, spatk: 31, spdef: 31, hp: 31 },
        evs: { atk: 85, def: 85, spd: 85, spatk: 85, spdef: 85, hp: 85 },
        moves: [''],
        heldItems: [{ name: '' }]
      }
    }
  },
  computed: {
    ...mapGetters(['randomSetsEntries', 'randomSetsTiers', 'randomBattleRulesBlacklists']),
    entries() {
      return this.randomSetsEntries || []
    },
    tiers() {
      return this.randomSetsTiers || []
    },
    tierOptions() {
      const sorted = [...this.tiers].sort((a, b) => {
        const aLevel = Number(a.level) || 0
        const bLevel = Number(b.level) || 0
        if (aLevel !== bLevel) return aLevel - bLevel
        return String(a.tier || '').localeCompare(String(b.tier || ''))
      })
      return [
        { value: '', text: 'Select tier...' },
        ...sorted.map(tier => ({ value: tier.id, text: `${tier.tier} (Lv ${tier.level})` }))
      ]
    },
    filteredEntries() {
      const query = this.quickFilter.trim().toLowerCase()
      return this.entries.filter(entry => {
        if (this.selectedTier && entry.tier_id !== this.selectedTier) return false
        if (this.activeFilter === 'active' && !entry.active) return false
        if (this.activeFilter === 'mega' && !entry.is_mega) return false
        if (this.activeFilter === 'zmove' && !entry.is_z_move) return false
        if (this.activeFilter === 'hazard' && !entry.is_hazard) return false
        if (!query) return true
        return this.buildSearchText(entry).toLowerCase().includes(query)
      })
    },
    pagedEntries() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredEntries.slice(start, start + this.perPage)
    },
    selectedMonster() {
      if (!this.form.monsterId || !this.monsterMapId) return null
      return this.monsterMapId.get(this.form.monsterId) || null
    },
    abilityOptions() {
      if (!this.selectedMonster || !Array.isArray(this.selectedMonster.Ability)) return []
      return this.selectedMonster.Ability.filter(ability => ability && ability !== 'None')
    },
    abilitySelectOptions() {
      return [
        { value: '', text: 'Select ability...' },
        ...this.abilityOptions.map(ability => ({ value: ability, text: ability }))
      ]
    },
    formPokemonLabel() {
      if (!this.form.monsterId) return 'Select a Pokemon'
      return this.getLookupName(this.form.monsterId, this.monsterMapName, this.monsterMapId)
    },
    formAbilityLabel() {
      return this.form.abilityName ? this.form.abilityName : 'Select ability'
    },
    formMovesLabel() {
      const names = this.form.moves
        .filter(move => move !== '' && move !== null && move !== undefined)
        .map(move => this.getLookupName(move, this.moveMapName, this.moveMapId))
      return names.length ? names.join(', ') : 'Select moves'
    },
    formItemsLabel() {
      const items = this.form.heldItems
        .map(item => String(item.name || '').trim())
        .filter(name => name.length > 0)
        .map(name => this.getItemLabelFromName(name))
      return items.length ? items.join(', ') : 'None'
    },
    monsterValidation() {
      return this.validateSelectedLookup(this.form.monsterId, this.monsterInput, this.monsterMapName, this.monsterMapId, 'Pokemon')
    },
    monsterState() {
      if (!this.validationActive) return null
      return this.monsterValidation.valid ? null : false
    },
    tierValidation() {
      const isValid = !!this.form.tierId
      return { valid: isValid, message: 'Tier is required.' }
    },
    tierState() {
      if (!this.validationActive) return null
      return this.tierValidation.valid ? null : false
    },
    abilityValidation() {
      const ability = String(this.form.abilityName || '').trim()
      if (!ability) return { valid: false, message: 'Ability is required.' }
      const valid = this.abilityOptions.some(opt => opt.toLowerCase() === ability.toLowerCase())
      return { valid, message: 'Ability must match the selected Pokemon.' }
    },
    abilityState() {
      if (!this.validationActive) return null
      return this.abilityValidation.valid ? null : false
    },
    moveValidation() {
      return this.form.moves.map((value, index) => (
        this.validateSelectedLookup(value, this.moveInputs[index], this.moveMapName, this.moveMapId, 'Move')
      ))
    },
    moveStates() {
      if (!this.validationActive) return this.moveValidation.map(() => null)
      return this.moveValidation.map(result => (result.valid ? null : false))
    },
    movesHaveError() {
      if (!this.validationActive) return false
      const validMoves = this.form.moves.filter(move => move)
      if (!validMoves.length) return true
      return this.moveValidation.some(result => !result.valid)
    },
    heldItemValidation() {
      return this.form.heldItems.map(item => {
        const name = String(item.name || '').trim()
        if (!name) return { valid: true }
        const numeric = parseInt(name, 10)
        if (!Number.isNaN(numeric)) {
          const exists = this.itemMapId ? this.itemMapId.has(numeric) : true
          return exists ? { valid: true } : { valid: false, message: 'Item must be selected from the list.' }
        }
        const found = this.itemMapName && this.itemMapName.has(name.toLowerCase())
        return found ? { valid: true } : { valid: false, message: 'Item must be selected from the list.' }
      })
    },
    heldItemStates() {
      if (!this.validationActive) return this.heldItemValidation.map(() => null)
      return this.heldItemValidation.map(result => (result.valid ? null : false))
    },
    heldItemsHaveError() {
      if (!this.validationActive) return false
      return this.heldItemValidation.some(result => !result.valid)
    },
    firstInvalidMessage() {
      const validations = [
        this.monsterValidation,
        this.tierValidation,
        this.abilityValidation,
        ...this.moveValidation,
        ...this.heldItemValidation
      ]
      const first = validations.find(result => result && result.valid === false)
      return first ? (first.message || 'Please correct the highlighted fields.') : ''
    },
    isFormValid() {
      const validations = [
        this.monsterValidation,
        this.tierValidation,
        this.abilityValidation,
        ...this.moveValidation,
        ...this.heldItemValidation
      ]
      const validMoves = this.form.moves.filter(move => move)
      return validations.every(result => result.valid !== false) && validMoves.length > 0
    }
  },
  watch: {
    randomSetsEntries() {
      this.loading = false
      this.saving = false
      this.deletingId = null
      this.editingId = null
      this.currentPage = 1
    },
    randomBattleRulesBlacklists: {
      deep: true,
      handler() {
        this.rulesSaving = false
        this.setRulesFormFromStore()
      }
    },
    filteredEntries() {
      const totalPages = Math.max(1, Math.ceil(this.filteredEntries.length / this.perPage))
      if (this.currentPage > totalPages) {
        this.currentPage = totalPages
      }
    },
    selectedMonster() {
      this.updateAbilityDefaults()
    },
    'form.heldItems': {
      deep: true,
      handler() {
        this.autoDetectFlags()
      }
    },
      'form.moves': {
        deep: true,
        handler() {
          if (this.editingId || this.suspendHazardAutoDetect) return
          this.autoDetectHazardFromMoves()
        }
      }
  },
  created() {
    this.buildLookups()
    this.emit('getRandomBattleRules', {})
    this.refresh()
  },
  methods: {
    buildLookups() {
      this.itemMapName = new Map()
      this.itemMapId = new Map()
      if (this.items) {
        this.items.forEach(item => {
          this.itemMapName.set(item.Name.toLowerCase(), item)
          this.itemMapId.set(item.ID, item)
        })
      }

      this.monsterMapName = new Map()
      this.monsterMapId = new Map()
      if (this.monsters) {
        this.monsters.forEach(monster => {
          this.monsterMapName.set(monster.Name.toLowerCase(), monster)
          this.monsterMapId.set(monster.ID, monster)
        })
      }

      this.moveMapName = new Map()
      this.moveMapId = new Map()
      if (this.moves) {
        this.moves.forEach(move => {
          this.moveMapName.set(move.Name.toLowerCase(), move)
          this.moveMapId.set(move.ID, move)
        })
      }
    },
    refresh() {
      this.loading = true
      this.emit('getRandomSets', this.activeServer)
    },
    uploadRandomTeams() {
      this.$bvModal.msgBoxConfirm('Upload random teams to live servers?', {
        title: 'Upload Random Teams',
        okVariant: 'info',
        okTitle: 'Upload',
        cancelTitle: 'Cancel'
      }).then(value => {
        if (value) {
          this.emit('reloadRandomTeams', this.activeServer)
        }
      })
    },
    applyRules() {
      this.$bvModal.msgBoxConfirm('Apply Random Battle rules to Gold and Silver?', {
        title: 'Apply Random Battle Rules',
        okVariant: 'warning',
        okTitle: 'Apply Set Rules',
        cancelTitle: 'Cancel'
      }).then(value => {
        if (value) {
          this.emit('applyRandomBattleRules', {})
        }
      })
    },
    openRulesModal() {
      this.setRulesFormFromStore()
      this.showRulesModal = true
      this.emit('getRandomBattleRules', {})
    },
    setRulesFormFromStore() {
      const blacklists = this.randomBattleRulesBlacklists || {}
      this.rulesForm = {
        moves: this.formatBlacklistText(blacklists.moves),
        abilities: this.formatBlacklistText(blacklists.abilities),
        items: this.formatBlacklistText(blacklists.items)
      }
    },
    formatBlacklistText(values) {
      return Array.isArray(values) ? values.join('\n') : ''
    },
    parseBlacklistText(value) {
      const seen = new Set()
      return String(value || '')
        .split(/\r?\n/)
        .map(item => item.trim())
        .filter(item => {
          if (!item) return false
          const key = item.toLowerCase()
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
    },
    saveRulesBlacklists() {
      this.rulesSaving = true
      this.emit('saveRandomBattleRules', {
        moves: this.parseBlacklistText(this.rulesForm.moves),
        abilities: this.parseBlacklistText(this.rulesForm.abilities),
        items: this.parseBlacklistText(this.rulesForm.items)
      })
    },
      openAddModal() {
        this.resetForm()
        this.showAddModal = true
      },
      openCloneModal(entry) {
        if (!entry) return
        this.editingId = null
        this.suspendHazardAutoDetect = true
        this.invalidShake = false
        this.validationActive = false
        this.form = {
          monsterId: entry.monster_id,
          abilityName: entry.ability_name || '',
          tierId: entry.tier_id,
          isMega: !!entry.is_mega,
          isZMove: !!entry.is_z_move,
          isHazard: !!entry.is_hazard,
          active: !!entry.active,
          ivs: {
            atk: entry.iv_atk,
            def: entry.iv_def,
            spd: entry.iv_spd,
            spatk: entry.iv_spatk,
            spdef: entry.iv_spdef,
            hp: entry.iv_hp
          },
          evs: {
            atk: entry.ev_atk,
            def: entry.ev_def,
            spd: entry.ev_spd,
            spatk: entry.ev_spatk,
            spdef: entry.ev_spdef,
            hp: entry.ev_hp
          },
          moves: (entry.skills || []).map(skill => skill.skill_id),
          heldItems: (entry.held_items || []).map(item => {
            const name = item.held_item_name || this.resolveItemName(item.held_item_id)
            return { name: name || '' }
          })
        }
        if (!this.form.moves.length) this.form.moves = ['']
        if (!this.form.heldItems.length) this.form.heldItems = [{ name: '' }]
        this.moveInputs = this.form.moves.map(() => '')
        this.showAddModal = true
        this.$nextTick(() => {
          this.suspendHazardAutoDetect = false
        })
      },
      openEditModal(entry) {
        this.editingId = entry.uid
        this.invalidShake = false
      this.validationActive = false
      this.form = {
        monsterId: entry.monster_id,
        abilityName: entry.ability_name || '',
        tierId: entry.tier_id,
        isMega: !!entry.is_mega,
        isZMove: !!entry.is_z_move,
        isHazard: !!entry.is_hazard,
        active: !!entry.active,
        ivs: {
          atk: entry.iv_atk,
          def: entry.iv_def,
          spd: entry.iv_spd,
          spatk: entry.iv_spatk,
          spdef: entry.iv_spdef,
          hp: entry.iv_hp
        },
        evs: {
          atk: entry.ev_atk,
          def: entry.ev_def,
          spd: entry.ev_spd,
          spatk: entry.ev_spatk,
          spdef: entry.ev_spdef,
          hp: entry.ev_hp
        },
        moves: (entry.skills || []).map(skill => skill.skill_id),
        heldItems: (entry.held_items || []).map(item => {
          const name = item.held_item_name || this.resolveItemName(item.held_item_id)
          return { name: name || '' }
        })
      }
      if (!this.form.moves.length) this.form.moves = ['']
      if (!this.form.heldItems.length) this.form.heldItems = [{ name: '' }]
      this.moveInputs = this.form.moves.map(() => '')
      this.showAddModal = true
    },
      resetForm() {
        this.editingId = null
        this.suspendHazardAutoDetect = false
        this.invalidShake = false
        this.validationActive = false
      this.form = {
        monsterId: '',
        abilityName: '',
        tierId: '',
        isMega: false,
        isZMove: false,
        isHazard: false,
        active: true,
        ivs: { atk: 31, def: 31, spd: 31, spatk: 31, spdef: 31, hp: 31 },
        evs: { atk: 85, def: 85, spd: 85, spatk: 85, spdef: 85, hp: 85 },
        moves: [''],
        heldItems: [{ name: '' }]
      }
      this.monsterInput = ''
      this.moveInputs = ['']
    },
    submitSet() {
      this.validationActive = true
      if (!this.isFormValid) {
        this.triggerInvalidAnimation()
        this.showInfoModal('Invalid Fields', this.firstInvalidMessage || 'Please correct the highlighted fields.')
        return
      }
      const payload = this.buildPayload()
      if (!payload) return
      this.saving = true
      this.loading = true
      if (this.editingId) {
        payload.uid = this.editingId
        this.emit('editRandomSet', payload)
      } else {
        this.emit('addRandomSet', payload)
      }
      this.showAddModal = false
    },
    confirmDelete(entry) {
      if (!entry || !entry.uid) return
      this.$bvModal.msgBoxConfirm(`Delete random set UID #${entry.uid}?`, {
        title: 'Delete Set',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel'
      }).then(value => {
        if (value) {
          this.deletingId = entry.uid
          this.loading = true
          this.emit('deleteRandomSet', { uid: entry.uid, server: this.activeServer })
        }
      })
    },
    addMove() {
      if (this.form.moves.length >= 4) return
      this.form.moves.push('')
      this.moveInputs.push('')
    },
    removeMove(index) {
      if (this.form.moves.length <= 1) return
      this.form.moves.splice(index, 1)
      this.moveInputs.splice(index, 1)
    },
    addItem() {
      this.form.heldItems.push({ name: '' })
    },
    removeItem(index) {
      if (this.form.heldItems.length <= 1) return
      this.form.heldItems.splice(index, 1)
    },
    normalizeInputValue(value) {
      if (value && value.target && typeof value.target.value === 'string') {
        return value.target.value
      }
      if (value === null || value === undefined) return ''
      return String(value)
    },
    onMonsterChange(value) {
      this.monsterInput = this.normalizeInputValue(value)
    },
    onMonsterSelected() {
      this.monsterInput = ''
    },
    onMoveChange(index, value) {
      this.$set(this.moveInputs, index, this.normalizeInputValue(value))
    },
    onMoveSelected(index) {
      this.$set(this.moveInputs, index, '')
    },
    updateAbilityDefaults() {
      const abilityOptions = this.abilityOptions
      if (!abilityOptions.length) {
        this.form.abilityName = ''
        return
      }
      const current = String(this.form.abilityName || '').trim()
      const stillValid = abilityOptions.some(opt => opt.toLowerCase() === current.toLowerCase())
      if (!stillValid) {
        this.form.abilityName = abilityOptions[0]
      }
    },
    toggleFlag(key) {
      if (!key || !Object.prototype.hasOwnProperty.call(this.form, key)) return
      this.form[key] = !this.form[key]
    },
    triggerInvalidAnimation() {
      this.invalidShake = true
      setTimeout(() => {
        this.invalidShake = false
      }, 420)
    },
    validateSelectedLookup(value, rawInput, mapName, mapId, label) {
      const raw = String(rawInput || '').trim()
      const selectedEmpty = value === null || value === undefined || value === '' || value === 0
      if (raw && selectedEmpty) {
        return { valid: false, message: `${label} must be selected from the list.` }
      }
      return this.validateLookup(value, mapName, mapId, label)
    },
    validateLookup(value, mapName, mapId, label) {
      const raw = value === null || value === undefined ? '' : value
      if (raw === '' || raw === 0) return { valid: true, empty: true }
      const numeric = parseInt(raw, 10)
      if (!Number.isNaN(numeric)) {
        const exists = mapId ? mapId.has(numeric) : true
        return exists ? { valid: true } : { valid: false, message: `${label} must be selected from the list.` }
      }
      const key = String(raw || '').toLowerCase().trim()
      if (!key) return { valid: true, empty: true }
      const exists = mapName ? mapName.has(key) : true
      return exists ? { valid: true } : { valid: false, message: `${label} must be selected from the list.` }
    },
    buildPayload() {
      const monsterId = this.normalizeId(this.form.monsterId, 'Pokemon')
      if (monsterId === null) return null
      const abilityIndex = this.resolveAbilityIndex()
      if (!abilityIndex) {
        this.showInfoModal('Invalid Ability', 'Ability must match the selected Pokemon.')
        return null
      }
      const tierId = this.form.tierId ? Number(this.form.tierId) : 0
      if (!tierId) {
        this.showInfoModal('Invalid Tier', 'Tier is required.')
        return null
      }

      const moveIds = []
      for (let i = 0; i < this.form.moves.length; i += 1) {
        const moveId = this.normalizeId(this.form.moves[i], `Move ${i + 1}`)
        if (moveId === null) return null
        if (moveId > 0) moveIds.push(moveId)
      }

      const heldItems = []
      for (let i = 0; i < this.form.heldItems.length; i += 1) {
        const item = this.form.heldItems[i]
        const itemName = String(item.name || '').trim()
        if (!itemName) continue
        const itemId = this.resolveItemId(itemName)
        if (itemId === null) return null
        heldItems.push({
          held_item_id: itemId,
          skill_id: 0,
          priority: 0
        })
      }

      return {
        server: this.activeServer,
        monster_id: monsterId,
        ability: abilityIndex,
        tier_id: tierId,
        is_mega: this.form.isMega ? 1 : 0,
        is_z_move: this.form.isZMove ? 1 : 0,
        is_hazard: this.form.isHazard ? 1 : 0,
        active: this.form.active ? 1 : 0,
        iv_atk: this.form.ivs.atk,
        iv_def: this.form.ivs.def,
        iv_spd: this.form.ivs.spd,
        iv_spatk: this.form.ivs.spatk,
        iv_spdef: this.form.ivs.spdef,
        iv_hp: this.form.ivs.hp,
        ev_atk: this.form.evs.atk,
        ev_def: this.form.evs.def,
        ev_spd: this.form.evs.spd,
        ev_spatk: this.form.evs.spatk,
        ev_spdef: this.form.evs.spdef,
        ev_hp: this.form.evs.hp,
        skills: moveIds.map(skillId => ({ skill_id: skillId })),
        held_items: heldItems
      }
    },
    resolveAbilityIndex() {
      const ability = String(this.form.abilityName || '').trim()
      if (!ability || !this.selectedMonster || !Array.isArray(this.selectedMonster.Ability)) return 0
      for (let i = 0; i < this.selectedMonster.Ability.length; i += 1) {
        const option = this.selectedMonster.Ability[i]
        if (option && option.toLowerCase() === ability.toLowerCase()) {
          return i + 1
        }
      }
      return 0
    },
    resolveItemId(name) {
      const value = (name || '').trim()
      if (!value) return 0
      const numeric = parseInt(value, 10)
      if (!Number.isNaN(numeric)) return numeric
      const found = this.itemMapName.get(value.toLowerCase())
      if (!found) {
        this.showInfoModal('Invalid Item', `Unknown item: ${value}`)
        return null
      }
      return found.ID
    },
    resolveItemName(value) {
      const raw = String(value || '').trim()
      if (!raw) return ''
      const numeric = parseInt(raw, 10)
      if (!Number.isNaN(numeric)) {
        const found = this.itemMapId && this.itemMapId.get(numeric)
        return found ? found.Name : ''
      }
      return raw
    },
    resolveMoveName(value) {
      const raw = String(value || '').trim()
      if (!raw) return ''
      const numeric = parseInt(raw, 10)
      if (!Number.isNaN(numeric)) {
        const found = this.moveMapId && this.moveMapId.get(numeric)
        return found ? found.Name : ''
      }
      const found = this.moveMapName && this.moveMapName.get(raw.toLowerCase())
      return found ? found.Name : raw
    },
    autoDetectFlags() {
      const items = this.form.heldItems || []
      let hasMega = false
      let hasZ = false
      for (let i = 0; i < items.length; i += 1) {
        const name = this.resolveItemName(items[i].name)
        if (!name) continue
        if (this.isMegaItemName(name)) hasMega = true
        if (this.isZItemName(name)) hasZ = true
      }
      this.form.isMega = hasMega
      this.form.isZMove = hasZ
    },
    autoDetectHazardFromMoves() {
      const moves = this.form.moves || []
      let hasHazard = false
      for (let i = 0; i < moves.length; i += 1) {
        const name = this.resolveMoveName(moves[i])
        if (this.isHazardMoveName(name)) {
          hasHazard = true
          break
        }
      }
      this.form.isHazard = hasHazard
    },
    isHazardMoveName(name) {
      const normalized = String(name || '').trim().toLowerCase()
      if (!normalized) return false
      return HAZARD_MOVES.has(normalized)
    },
    isMegaItemName(name) {
      const normalized = String(name || '').trim().toLowerCase()
      if (!normalized) return false
      if (normalized === 'eviolite') return false
      if (normalized.includes('mega stone')) return true
      return /ite(\s*x|\s*y)?$/.test(normalized)
    },
    isZItemName(name) {
      const normalized = String(name || '').trim().toLowerCase()
      if (!normalized) return false
      if (normalized.includes('z-crystal') || normalized.includes('z crystal')) return true
      if (normalized.includes('crystal')) return false
      return /(?:\s|-|_)z$/.test(normalized)
    },
    normalizeId(value, label) {
      if (value === null || value === undefined || value === '') return 0
      if (typeof value === 'number') return value
      const numeric = parseInt(value, 10)
      if (!Number.isNaN(numeric)) return numeric
      this.showInfoModal('Invalid Value', `${label} must be selected from the list.`)
      return null
    },
    buildSearchText(entry) {
      return [
        entry.monster_name,
        entry.tier_name,
        entry.ability_name,
        this.formatMoves(entry),
        this.formatItems(entry),
        this.formatFlags(entry)
      ].join(' ')
    },
    formatMoves(entry) {
      if (!entry.skills || !entry.skills.length) return 'None'
      return entry.skills.map(skill => skill.skill_name || `#${skill.skill_id}`).join(', ')
    },
    formatItems(entry) {
      if (!entry.held_items || !entry.held_items.length) return 'None'
      return entry.held_items.map(item => (
        item.held_item_name || `#${item.held_item_id}`
      )).join(', ')
    },
    formatFlags(entry) {
      const flags = []
      if (entry.is_mega) flags.push('Mega')
      if (entry.is_z_move) flags.push('Z-Move')
      if (entry.is_hazard) flags.push('Hazard')
      return flags.length ? flags.join(', ') : 'None'
    },
    flagBadgeText(entry) {
      if (entry.is_mega) return 'Mega'
      if (entry.is_z_move) return 'Z-Move'
      if (entry.is_hazard) return 'Hazard'
      return 'Standard'
    },
    flagBadgeClass(entry) {
      if (entry.is_mega) return 'flag-mega'
      if (entry.is_z_move) return 'flag-zmove'
      if (entry.is_hazard) return 'flag-hazard'
      return 'flag-standard'
    },
    formatIvs(entry) {
      return `${entry.iv_hp}/${entry.iv_atk}/${entry.iv_def}/${entry.iv_spatk}/${entry.iv_spdef}/${entry.iv_spd}`
    },
    formatEvs(entry) {
      return `${entry.ev_hp}/${entry.ev_atk}/${entry.ev_def}/${entry.ev_spatk}/${entry.ev_spdef}/${entry.ev_spd}`
    },
    getTierName(id) {
      const found = this.tiers.find(tier => tier.id === id)
      return found ? found.tier : 'Unknown'
    },
    getTierLevel(id) {
      const found = this.tiers.find(tier => tier.id === id)
      return found ? found.level : '-'
    },
    getLookupName(value, mapName, mapId) {
      const numeric = parseInt(value, 10)
      if (!Number.isNaN(numeric)) {
        const byId = mapId.get(numeric)
        return byId ? `${byId.Name} (#${numeric})` : `#${numeric}`
      }
      const byName = mapName.get(String(value || '').toLowerCase())
      return byName ? `${byName.Name} (#${byName.ID})` : value
    },
    getItemLabelFromName(value) {
      const numeric = parseInt(value, 10)
      if (!Number.isNaN(numeric)) return `#${numeric}`
      const found = this.itemMapName.get(String(value || '').toLowerCase())
      if (!found) return value
      return `${found.Name} (#${found.ID})`
    },
    getItemSpriteUrl(value) {
      const name = this.resolveItemName(value)
      if (!name) return ''
      return this.getItemSpriteUrlByName(name)
    },
    getItemSpriteUrlByName(name) {
      if (!name) return ''
      const slug = this.slugifyItemName(name)
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${slug}.png`
    },
    slugifyItemName(name) {
      return String(name || '')
        .toLowerCase()
        .replace(/'/g, '')
        .replace(/\./g, '')
        .replace(/:/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()
    },
    getPokemonSpriteUrl(id) {
      const numeric = Number(id)
      if (!numeric || Number.isNaN(numeric)) return ''
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${numeric}.png`
    },
    getPokemonFallbackId(id) {
      const numeric = Number(id)
      if (!numeric || Number.isNaN(numeric) || !this.monsterMapId) return ''
      const current = this.monsterMapId.get(numeric)
      if (!current || !current.Name) return ''
      const baseName = this.normalizePokemonName(current.Name)
      if (!baseName || baseName === current.Name) return ''
      const base = this.monsterMapName.get(baseName.toLowerCase())
      if (!base || base.ID === id) return ''
      return base.ID
    },
    normalizePokemonName(name) {
      const raw = String(name || '')
      if (raw.startsWith('Rotom-') || raw.startsWith('Rotom ')) {
        return 'Rotom'
      }
      return raw
        .replace(/\b(Alola|Alolan|Galarian|Hisuian|Paldean|Mega|Primal|Origin|Therian|Incarnate|Forme?|Mode|Sky|Midnight|Dusk|Dawn|Ultra|Gigantamax|Gmax|Fan|Wash|Heat|Frost|Mow)\b/gi, '')
        .replace(/\b(Attack|Defense|Speed|Complete|Ash|Black|White)\b/gi, '')
        .replace(/[-:]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    handleIconError(event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.getPokemonSpriteUrl(fallbackId)
        target.dataset.fallbackId = ''
        return
      }
      target.style.display = 'none'
    },
    showInfoModal(title, message) {
      this.$bvModal.msgBoxOk(message, {
        title: title,
        okVariant: 'primary',
        okTitle: 'OK'
      })
    }
  }
}
</script>

<style scoped>
.random-sets-view .sets-card {
  border-radius: 14px;
}

.sets-header {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.sets-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: stretch;
}

.toolbar-left {
  flex: 1 1 420px;
  max-width: 700px;
}

.controls-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(16, 19, 23, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.controls-panel-grow {
  flex: 0 1 700px;
  width: 100%;
}

.controls-panel-actions {
  min-width: 320px;
  flex: 0 1 420px;
}


.controls-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 700;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tier-filter {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
}

.tier-filter small {
  font-weight: 700;
  color: rgba(255, 255, 255, 0.72);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: rgba(24, 28, 34, 0.6);
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
}

.filter-pill.filter-all {
  border-color: rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.75);
}

.filter-pill.filter-active {
  border-color: rgba(111, 66, 193, 0.45);
  color: #d9c9ff;
}

.filter-pill.filter-mega {
  border-color: rgba(40, 167, 69, 0.45);
  color: #b6f1c1;
}

.filter-pill.filter-zmove {
  border-color: rgba(32, 168, 216, 0.45);
  color: #bfe9f6;
}

.filter-pill.filter-hazard {
  border-color: rgba(255, 193, 7, 0.5);
  color: #ffe4a1;
}

.filter-pill.active {
  background: rgba(32, 168, 216, 0.2);
  color: #ccefff;
  border-color: rgba(32, 168, 216, 0.6);
  box-shadow: 0 0 12px rgba(32, 168, 216, 0.2);
}

.filter-pill.active.filter-all {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.35);
  color: #fff;
  box-shadow: none;
}

.filter-pill.active.filter-active {
  background: rgba(111, 66, 193, 0.2);
  border-color: rgba(111, 66, 193, 0.6);
  color: #d9c9ff;
  box-shadow: 0 0 12px rgba(111, 66, 193, 0.2);
}

.filter-pill.active.filter-mega {
  background: rgba(40, 167, 69, 0.2);
  border-color: rgba(40, 167, 69, 0.6);
  color: #b6f1c1;
  box-shadow: 0 0 12px rgba(40, 167, 69, 0.2);
}

.filter-pill.active.filter-zmove {
  background: rgba(32, 168, 216, 0.2);
  border-color: rgba(32, 168, 216, 0.6);
  color: #bfe9f6;
  box-shadow: 0 0 12px rgba(32, 168, 216, 0.2);
}

.filter-pill.active.filter-hazard {
  background: rgba(255, 193, 7, 0.2);
  border-color: rgba(255, 193, 7, 0.6);
  color: #ffe4a1;
  box-shadow: 0 0 12px rgba(255, 193, 7, 0.2);
}

.action-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search-box-group {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #12161b;
}

.search-box-prepend {
  background: #12161b;
  border: 0;
  color: rgba(255, 255, 255, 0.55);
  padding: 0 12px;
  display: flex;
  align-items: center;
}

.search-box-input {
  background: #12161b;
  border: 0;
  color: #fff;
  font-weight: 600;
  padding: 8px 10px;
}

.sets-toolbar select,
.sets-toolbar .custom-select {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.search-box-input::placeholder {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.45);
}

.search-clear {
  border: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  background: #12161b;
  color: rgba(255, 255, 255, 0.6);
}

.sets-list-wrapper {
  position: relative;
  min-height: 120px;
}

.sets-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 12, 15, 0.7);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
}

.set-card {
  border-radius: 14px;
  padding: 18px 18px 16px;
  background: linear-gradient(145deg, rgba(27, 31, 36, 0.95), rgba(22, 25, 30, 0.95));
}

.set-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}

.set-header-main {
  display: flex;
  gap: 10px;
  align-items: center;
}


.set-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.set-icon img {
  width: 24px;
  height: 24px;
  image-rendering: pixelated;
}

.set-icon i {
  position: absolute;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.55);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.set-icon img[style*="display: none"] + i {
  opacity: 1;
}

.set-name {
  font-weight: 700;
  font-size: 1rem;
}

.set-meta {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.55);
}

.set-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.set-flag-badge {
  margin-left: auto;
  margin-right: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.flag-mega {
  background: rgba(40, 167, 69, 0.2);
  color: #b6f1c1;
}

.flag-zmove {
  background: rgba(32, 168, 216, 0.2);
  color: #bfe9f6;
}

.flag-hazard {
  background: rgba(255, 193, 7, 0.2);
  color: #ffe4a1;
}

.flag-standard {
  background: rgba(108, 117, 125, 0.2);
  color: rgba(255, 255, 255, 0.7);
}

.set-badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.badge-live {
  background: rgba(40, 167, 69, 0.2);
  color: #b6f1c1;
}

.badge-muted {
  background: rgba(108, 117, 125, 0.2);
  color: rgba(255, 255, 255, 0.6);
}

.set-action-btn {
  color: rgba(255, 255, 255, 0.5);
  padding: 0 4px;
}

.set-action-btn:hover {
  color: #63c2de;
}

.set-details {
  display: grid;
  gap: 8px;
}


.set-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.set-label {
  width: 80px;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.45);
}

.set-value {
  font-size: 0.84rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.35;
}

.preview-card {
  background: rgba(24, 28, 34, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 14px 16px;
}

.preview-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 8px;
}

.preview-summary {
  font-weight: 700;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.preview-explain {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 10px;
}

.preview-details {
  display: grid;
  gap: 6px;
}

.preview-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.preview-label {
  width: 100px;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.45);
  margin-right: 8px;
}

.preview-value {
  font-size: 0.84rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
}

.rules-modal-body {
  color: rgba(255, 255, 255, 0.86);
}

.rules-panel {
  background: rgba(24, 28, 34, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 14px 16px;
}

.rules-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.58);
  margin-bottom: 8px;
}

.rules-list {
  margin: 0;
  padding-left: 20px;
  font-size: 0.82rem;
  line-height: 1.55;
  columns: 2;
  column-gap: 32px;
}

.rules-list li {
  break-inside: avoid;
  page-break-inside: avoid;
  margin-bottom: 2px;
}

.blacklist-row {
  margin-left: -10px;
  margin-right: -10px;
}

.rules-textarea {
  background: #12161b;
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.8rem;
  line-height: 1.35;
  min-height: 420px;
}

.add-modal-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-row {
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.form-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.form-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
}

.flag-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.flag-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(24, 28, 34, 0.6);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  transition: all 0.2s ease;
}

.flag-tag:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

.flag-tag.active {
  background: rgba(32, 168, 216, 0.2);
  border-color: rgba(32, 168, 216, 0.6);
  color: #ccefff;
  box-shadow: 0 0 12px rgba(32, 168, 216, 0.2);
}

.flag-tag.active.flag-mega {
  background: rgba(40, 167, 69, 0.2);
  border-color: rgba(40, 167, 69, 0.6);
  color: #b6f1c1;
  box-shadow: 0 0 12px rgba(40, 167, 69, 0.2);
}

.flag-tag.active.flag-zmove {
  background: rgba(32, 168, 216, 0.2);
  border-color: rgba(32, 168, 216, 0.6);
  color: #ccefff;
  box-shadow: 0 0 12px rgba(32, 168, 216, 0.2);
}

.flag-tag.active.flag-hazard {
  background: rgba(255, 193, 7, 0.2);
  border-color: rgba(255, 193, 7, 0.6);
  color: #ffe4a1;
  box-shadow: 0 0 12px rgba(255, 193, 7, 0.2);
}

.flag-tag.active.flag-active {
  background: rgba(111, 66, 193, 0.2);
  border-color: rgba(111, 66, 193, 0.6);
  color: #d9c9ff;
  box-shadow: 0 0 12px rgba(111, 66, 193, 0.2);
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.stat-cell label {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 4px;
}

.moves-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.move-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.list-field {
  flex: 1 1 auto;
  min-width: 0;
}

.item-icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: linear-gradient(150deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.04));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  position: relative;
  flex: 0 0 28px;
}

.item-icon img {
  width: 22px;
  height: 22px;
  image-rendering: pixelated;
}

.item-icon i {
  position: absolute;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.55);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.item-icon img[src=""] {
  display: none;
}

.item-icon img[style*="display: none"] + i,
.item-icon img[style*="display:none"] + i,
.item-icon img[src=""] + i {
  opacity: 1;
}


.mini-icon-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
}

.action-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: rgba(32, 168, 216, 0.85);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.btn-disabled-soft {
  opacity: 0.6;
}

@media (min-width: 992px) {
  .sets-header {
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
  }
}
</style>

<style>
.random-battle-rules-modal .modal-dialog {
  max-width: min(1800px, calc(100vw - 32px));
}

.random-battle-rules-modal .modal-body {
  max-height: calc(100vh - 160px);
}

@media (max-width: 1199.98px) {
  .random-battle-rules-modal .rules-list {
    columns: 1;
  }
}
</style>
